import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";

interface MissingEmailModalProps {
  open: boolean;
  onClose: () => void;
}

const MissingEmailModal: React.FC<MissingEmailModalProps> = ({
  open,
  onClose,
}) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { api } = useApiClient();

  const handleSubmit = async () => {
    if (!email.trim()) {
      toast.warning("Email ID is required");
      return;
    }

    const emailRegex =
      /^(?=.*[a-zA-Z].*@|.*@.*[a-zA-Z].*\.)[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setIsLoading(true);
    try {
      await api.post(ENDPOINTS.PROFILE.EMAIL_CHANGE_REQUEST, {
        new_email: email.trim().toLowerCase(),
      });
      setSuccess(true);
      toast.success("Verification link sent!");
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      toast.error(
        error?.response?.data?.message || "Failed to update email. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="xs" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: "15px" }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Add Your Email
        </Typography>
      </DialogTitle>
      <DialogContent>
        {success ? (
          <Box py={2}>
            <Typography variant="body1" color="text.primary" gutterBottom>
              A verification link has been sent to <strong>{email}</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please check your inbox and verify your email to secure your account.
              You can now close this window.
            </Typography>
          </Box>
        ) : (
          <Box py={1}>
            <Typography variant="body2" color="text.secondary" mb={3}>
              We noticed you don't have an email associated with your account. 
              Adding an email ensures you can easily recover your password if you ever forget it.
            </Typography>
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder="e.g. user@example.com"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        {success ? (
          <Button onClick={onClose} variant="contained" fullWidth disableElevation>
            Done
          </Button>
        ) : (
          <>
            <Button
              onClick={handleClose}
              color="inherit"
              disabled={isLoading}
              sx={{ textTransform: "none" }}
            >
              I'll do this later
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isLoading}
              disableElevation
              sx={{ textTransform: "none" }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : "Verify Email"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MissingEmailModal;
